<template>
    <team-phone-number-pool-details :phoneNumberPoolId="props.phoneNumberPoolId">
        <div class="col-span-12 md:col-span-8 lg:col-span-10">
            <page-header> Phone Numbers </page-header>

            <vx-alert v-if="recentlyCreated" class="mb-10" color="success" :closeable="false" shadow>
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </template>
                <div class="flex items-center justify-between">
                    <span><strong>New Phone Pool</strong> created successfully. Manage numbers below.</span>
                    <vx-button :href="`/settings/teams/${teamId}#/phone-number-pools`" size="sm" color="success"
                        >Back to Phone Pools
                        <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon
                    ></vx-button>
                </div>
            </vx-alert>

            <team-phone-number-pool-phone-numbers-controls
                v-model="fetchParams"
                @reset="resetFilters"
                @apply="applyFilters"
            ></team-phone-number-pool-phone-numbers-controls>

            <template v-if="!loading && !list.length">
                <vx-box
                    v-if="!isEqual(defaultFilters(), fetchParams)"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    <div class="max-w-72 mx-auto text-center">
                        No results for current search, try adjusting your filters.
                    </div>
                    <vx-button type="button" color="secondary" @click="resetFilters">
                        Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                    </vx-button>
                </vx-box>
                <vx-box v-else color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    <template v-if="isImpersonatingOrPlatformAdmin">
                        <div class="max-w-96 mx-auto text-center">
                            There are no phone numbers in the pool. Add new numbers from the Phone Numbers view.
                        </div>
                        <vx-button :href="`/settings/teams/${teamId}#/phone-numbers`" color="muted" size="lg">
                            Add Phone Numbers
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                        </vx-button>
                    </template>
                    <template v-else> There are no phone numbers in the pool. </template>
                </vx-box>
            </template>

            <vx-table v-else-if="list.length" class="xl:grid-cols-5-auto" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div class="xl:col-span-2">Number</div>
                    <div>Number Type</div>
                    <div>Country</div>
                </vx-table-row>

                <team-phone-number-pool-phone-number-row
                    v-for="(item, index) in list"
                    v-model="list[index]"
                    :key="list[index].id"
                    @removed="removeFromList($event)"
                ></team-phone-number-pool-phone-number-row>
            </vx-table>

            <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
                <vx-button
                    v-if="fetchParams.cursor"
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </team-phone-number-pool-details>
</template>

<script setup>
import TeamPhoneNumberPoolDetails from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolDetails.vue';
import { faCircleCheck, faCircleX, faSquareArrowUpLeft, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '~/components/general/PageHeader.vue';
import { computed, onMounted } from 'vue';
import dayjs from '~/utils/dayjs';
import { usePhoneNumberPoolDetails } from '~/components/settings/teams/phone-number-pools/usePhoneNumberPoolDetails';
import { phoneNumberPoolsClient } from '~/services';
import { useCursorPagination } from '~/composables/useCursorPagination';
import TeamPhoneNumberPoolPhoneNumbersControls from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolPhoneNumbersControls.vue';
import TeamPhoneNumberPoolPhoneNumberRow from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolPhoneNumberRow.vue';
import { isEqual } from '~/utils/helpers';

const props = defineProps({
    phoneNumberPoolId: {
        type: Number,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;
const { phoneNumberPool } = usePhoneNumberPoolDetails();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const recentlyCreated = computed(() =>
    phoneNumberPool.value.created_at
        ? dayjs.utc(phoneNumberPool.value.created_at).isAfter(dayjs().subtract(15, 'seconds'))
        : false
);

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(
        phoneNumberPoolsClient.getPhonePoolNumbers(teamId, phoneNumberPool.value.id, { ...fetchParams.value })
    );
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
